/**
 * Block-based slide model — free-form canvas with 9-anchor positioning.
 * Used by BlockSlide (see types.ts). Pure types only; presets live in templates.ts.
 */

export type Anchor =
  | 'top-left'
  | 'top-center'
  | 'top-right'
  | 'center-left'
  | 'center'
  | 'center-right'
  | 'bottom-left'
  | 'bottom-center'
  | 'bottom-right';

export type ScrimDirection =
  | 'top'
  | 'bottom'
  | 'left'
  | 'right'
  | 'top-right'
  | 'bottom-right'
  | 'top-left'
  | 'bottom-left';

export interface ScrimStop {
  at: number;
  opacity: number;
}

export interface Scrim {
  direction: ScrimDirection;
  stops: ScrimStop[];
  /** Optional tint (default black) */
  color?: string;
}

export type TextBlockKind =
  | 'eyebrow'
  | 'headline'
  | 'subheadline'
  | 'lede'
  | 'body'
  | 'caption'
  | 'accent';

export interface TextBlock {
  id: string;
  kind: TextBlockKind;
  anchor: Anchor;
  x: number;
  y: number;
  w?: number;
  h?: number;
  color?: string;
  text: string;
  fontSize?: number;
  textAlign?: 'left' | 'center' | 'right';
}

export interface QrBlock {
  id: string;
  kind: 'qr';
  anchor: Anchor;
  x: number;
  y: number;
  w?: number;
  value: string;
  caption?: string;
}

export interface BulletsBlock {
  id: string;
  kind: 'bullets';
  anchor: Anchor;
  x: number;
  y: number;
  w?: number;
  /** Optional px size; when set, overrides token sizing for list text */
  fontSize?: number;
  items: string[];
}

export interface ConversationThreadLine {
  from: 'user' | 'robin';
  text: string;
}

export interface ConversationBlock {
  id: string;
  kind: 'conversation';
  anchor: Anchor;
  x: number;
  y: number;
  w?: number;
  /** Base px size for the whole thread (label, bubbles, footer scale together) */
  fontSize?: number;
  label?: string;
  thread: ConversationThreadLine[];
  footer?: string;
}

export interface LogoBlock {
  id: string;
  kind: 'logo';
  anchor: Anchor;
  x: number;
  y: number;
  src: string;
  opacity?: number;
  w?: number;
}

export type Block = TextBlock | QrBlock | BulletsBlock | ConversationBlock | LogoBlock;

/** Canonical (x%, y%) for each anchor — used for snap targets */
export const ANCHOR_POSITIONS: Record<Anchor, { x: number; y: number }> = {
  'top-left': { x: 8, y: 12 },
  'top-center': { x: 50, y: 12 },
  'top-right': { x: 92, y: 12 },
  'center-left': { x: 8, y: 50 },
  center: { x: 50, y: 50 },
  'center-right': { x: 92, y: 50 },
  'bottom-left': { x: 8, y: 88 },
  'bottom-center': { x: 50, y: 88 },
  'bottom-right': { x: 92, y: 88 },
};

export const ANCHORS: Anchor[] = [
  'top-left',
  'top-center',
  'top-right',
  'center-left',
  'center',
  'center-right',
  'bottom-left',
  'bottom-center',
  'bottom-right',
];

export function nearestAnchor(
  x: number,
  y: number,
  thresholdPct: number,
): { anchor: Anchor; x: number; y: number } | null {
  let best: { anchor: Anchor; x: number; y: number; d: number } | null = null;
  for (const anchor of ANCHORS) {
    const p = ANCHOR_POSITIONS[anchor];
    const d = Math.hypot(x - p.x, y - p.y);
    if (d <= thresholdPct && (!best || d < best.d)) {
      best = { anchor, x: p.x, y: p.y, d };
    }
  }
  return best ? { anchor: best.anchor, x: best.x, y: best.y } : null;
}

/** Always returns one of the 9 anchors — picks via the 1/3 grid for predictable snapping. */
export function pointerToAnchor(xPct: number, yPct: number): Anchor {
  const col = xPct < 33 ? 'left' : xPct < 66 ? 'center' : 'right';
  const row = yPct < 33 ? 'top' : yPct < 66 ? 'center' : 'bottom';
  if (col === 'center' && row === 'center') return 'center';
  return `${row}-${col}` as Anchor;
}

export function cycleAnchor(current: Anchor, dir: 1 | -1): Anchor {
  const i = ANCHORS.indexOf(current);
  const next = (i + dir + ANCHORS.length) % ANCHORS.length;
  return ANCHORS[next]!;
}

/**
 * Decompose an anchor into its (vertical, horizontal) parts.
 * "center" → ("center", "center"); "top-right" → ("top", "right"); etc.
 */
export function anchorParts(a: Anchor): {
  v: 'top' | 'center' | 'bottom';
  h: 'left' | 'center' | 'right';
} {
  if (a === 'center') return { v: 'center', h: 'center' };
  const [v, h] = a.split('-') as ['top' | 'center' | 'bottom', 'left' | 'center' | 'right'];
  return { v, h };
}

/** Mirror an anchor horizontally — left↔right, centre stays put. */
export function flipAnchorHorizontally(a: Anchor): Anchor {
  switch (a) {
    case 'top-left':
      return 'top-right';
    case 'top-right':
      return 'top-left';
    case 'center-left':
      return 'center-right';
    case 'center-right':
      return 'center-left';
    case 'bottom-left':
      return 'bottom-right';
    case 'bottom-right':
      return 'bottom-left';
    default:
      return a;
  }
}

/** Mirror a scrim direction horizontally. */
export function flipScrimDirectionHorizontally(d: ScrimDirection): ScrimDirection {
  switch (d) {
    case 'left':
      return 'right';
    case 'right':
      return 'left';
    case 'top-left':
      return 'top-right';
    case 'top-right':
      return 'top-left';
    case 'bottom-left':
      return 'bottom-right';
    case 'bottom-right':
      return 'bottom-left';
    default:
      return d;
  }
}
